import 'vuetify/styles'
import '@mdi/font/css/materialdesignicons.css'

import { createVuetify } from 'vuetify'
import { vi, en } from 'vuetify/locale'

import '@fontsource/nunito/400.css'
import '@fontsource/nunito/700.css'
import '@fontsource/nunito/800.css'

export type AppThemeName = 'light' | 'dark'
export type AppThemeColorOverrides = Partial<Record<string, string>>

export const appThemeColors = {
  light: {
    dark: false,
    colors: {
      primary: '#2563EB',
      secondary: '#475569',
      accent: '#0F766E',
      success: '#16A34A',
      warning: '#D97706',
      error: '#DC2626',
      info: '#0284C7',
      pink: '#E91E63',
      background: '#F7F8FA',
      surface: '#FFFFFF',
      'surface-variant': '#111827',
      // 'on-background': '#111827',
      // 'on-surface': '#1F2937',
      outline: '#D7DEE8',
    },
  },
  dark: {
    dark: true,
    colors: {
      primary: '#60A5FA',
      secondary: '#CBD5E1',
      accent: '#2DD4BF',
      success: '#22C55E',
      warning: '#F59E0B',
      error: '#F87171',
      info: '#38BDF8',
      pink: '#F48FB1',
      background: '#0F172A',
      surface: '#172033',
      'surface-variant': '#233047',
      'on-background': '#E5E7EB',
      'on-surface': '#F8FAFC',
      outline: '#334155',
    },
  },
  // Theme F&B cho trang đặt món công khai (webview) — sage/terracotta, không dùng xanh dương.
  webview: {
    dark: false,
    colors: {
      primary: '#687F64',
      secondary: '#D78355',
      accent: '#D78355',
      success: '#5F8563',
      warning: '#D97706',
      error: '#B05C4A',
      info: '#687F64',
      pink: '#E91E63',
      background: '#F6F3EB',
      surface: '#FFFDF8',
      'surface-variant': '#EAF0E6',
      'on-background': '#29342D',
      'on-surface': '#29342D',
      outline: '#E7E2D7',
    },
  },
} as const

export const appVuetifyDefaults = {
  VAppBar: {
    density: 'compact',
    rounded: 'sm',
  },
  VAlert: {
    density: 'compact',
    rounded: 'sm',
    variant: 'tonal',
  },
  VAutocomplete: {
    autocomplete: 'off',
    density: 'compact',
    hideDetails: 'auto',
    variant: 'outlined',
  },

  VCheckbox: {
    density: 'compact',
    hideDetails: 'auto',
  },
  VCombobox: {
    autocomplete: 'off',
    density: 'compact',
    hideDetails: 'auto',
    variant: 'outlined',
  },
  VDataTable: {
    density: 'compact',
    fixedHeader: true,
  },
  VDateInput: {
    density: 'compact',
    hideDetails: 'auto',
    persistentPlaceholder: true,
    variant: 'outlined',
  },
  VDialog: {
    maxWidth: 640,
    persistent: true,
  },
  VList: {
    density: 'compact',
  },
  VListItem: {
    density: 'compact',
    minHeight: 36,
  },
  VMenu: {
    density: 'compact',
  },
  VSelect: {
    autocomplete: 'off',
    density: 'compact',
    hideDetails: 'auto',
    variant: 'outlined',
    height: '30px'
  },
  VSwitch: {
    density: 'compact',
    hideDetails: 'auto',
  },
  VTabs: {
    density: 'compact',
  },
  VTooltip: {
    contentClass: 'app-tooltip--dark',
  },
  VTextarea: {
    autocomplete: 'off',
    counter: false,
    density: 'compact',
    hideDetails: 'auto',
    rows: 3,
    variant: 'outlined',
  },
  VTextField: {
    autocomplete: 'off',
    counter: false,
    density: 'compact',
    hideDetails: 'auto',
    variant: 'outlined',
  },
  VToolbar: {
    density: 'compact',
  },
} as const

if (typeof window !== 'undefined') {
  if (!window.innerWidth || window.innerWidth === 0) {
    Object.defineProperty(window, 'innerWidth', {
      writable: true,
      configurable: true,
      value: 1280,
    })
  }
  if (typeof document !== 'undefined' && document.documentElement && (!document.documentElement.clientWidth || document.documentElement.clientWidth === 0)) {
    Object.defineProperty(document.documentElement, 'clientWidth', {
      writable: true,
      configurable: true,
      value: 1280,
    })
  }
}

export const vuetify = createVuetify({
  display: {
    mobileBreakpoint: 'md',
  },
  ssr: {
    clientWidth: 1280,
  },
  locale: {
    locale: 'vi',
    fallback: 'en',
    messages: { vi, en },
  },
  theme: {
    defaultTheme: 'light',
    variations: {
      colors: ['primary', 'secondary', 'accent', 'surface'],
      lighten: 1,
      darken: 2,
    },
    themes: appThemeColors,
  },
  defaults: appVuetifyDefaults,
})

export function setVuetifyThemeMode(themeName: AppThemeName) {
  vuetify.theme.change(themeName)
}

export function updateVuetifyThemeColors(themeName: AppThemeName, colors: AppThemeColorOverrides) {
  Object.assign(vuetify.theme.themes.value[themeName]?.colors ?? {}, colors)
}
